import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import UserAccountPage from './pages/UserAccountPage';
import AdminDashboard from './pages/AdminDashboard';
import WalletPage from './pages/WalletPage';
import CompleteProfile from './pages/CompleteProfile';
import InstallApp from './components/InstallApp';
import InstallButton from './components/InstallButton';
import CheckoutPage from './pages/CheckoutPage';
import CafeTables from './pages/CafeTables';
import RestaurantMenu from './pages/RestaurantMenu';
import NotFound from './pages/NotFound'; // Import your NotFound component
import OrderConfirmation from './pages/OrderConfirmation';
import OwnerMenuEdit from './pages/OwnerMenuEdit';
import NavBar from './components/NavBar';
import { CacheProvider } from '@emotion/react';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AdminMenuEdit from './pages/AdminMenuEdit';
import ShopOwnerRequest from './pages/ShopOwnerRequest';
import DeveloperDocs from './pages/DeveloperDocs';
import ShareRestaurant from './pages/ShareRestaurant';
import AuthSuccess from './components/AuthSuccess';
import './styles/App.css';
import ProductDetailPage from './pages/ProductDetailPage';
import AdminUsers from './pages/AdminUsers';
import AdminOrdersPage from './pages/OrdersPage';
import AdminPrinters from './pages/AdminPrinters';
import PaymentSettings from './pages/PaymentSettings';
import OrderDetails from './pages/OrderDetails';
import ProtectedRoute from './components/ProtectedRoute';
import PublicRouter from './components/PublicRouter';
import ProtectedRouteWithoutCompletionCheck from './components/ProtectedRouteWithoutCompletionCheck';
import { CartProvider } from './context/CartContext';
import CartDrawer from './components/CartDrawer';
import { NotificationProvider } from './context/NotificationContext';
import { ConfirmProvider } from 'material-ui-confirm';
import { blue } from "@mui/material/colors";
import { arEG } from "@mui/material/locale"; // Arabic MUI support
import WelcomePopup from './components/WelcomePopup';
import rtlPlugin from 'stylis-plugin-rtl';
import { prefixer } from 'stylis';
import createCache from '@emotion/cache';
import AboutPage from './pages/AboutPage';
import PrintServiceClient from './pages/PrintServiceClient';
import { LanguageProvider, useLanguage } from './context/LanguageContext';
import { ChatProvider } from './context/ChatContext';
import ChatWidget from './components/ChatWidget';
import { AdminChatProvider } from './context/AdminChatContext';
import AdminChatPanel from './pages/AdminChatPanel';
import keywordsData from './lang/keywords.json';
import GlobalLoadingDialog from './components/GlobalLoadingDialog';
import GlobalStyles from './components/GlobalStyles';
import { theme } from './theme';
import UserOrdersListPage from './pages/UserOrdersListPage';
import LandingPage from './pages/LandingPage';

const App = () => (
  <LanguageProvider>
      <MainApp />
  </LanguageProvider>
);

const MainApp = () => {
    const { language,toggleLanguage } = useLanguage();
  //const lang = language === 'ar' ? 'ar-IQ' : 'en-US';

  const rtlCache = createCache({
    key: language === 'ar' ? 'muirtl' : 'mui',
    stylisPlugins: language === 'ar' ? [prefixer, rtlPlugin] : [],
  });

// Global Theme Setup
const blackYellowTheme = {
  direction: language === 'ar' ? 'rtl' : 'ltr',
  palette: {
    mode: 'dark',
    primary: {
      main: '#000000',        // Pure black
      light: '#1A1A1A',       // Soft black
      dark: '#000000',        // Deep black
      contrastText: '#ffbb00', // Yellow text on black
    },
    secondary: {
      main: '#ffbb00',        // Bright yellow
      light: '#FFE55C',       // Soft yellow
      dark: '#DAA520',        // Deep gold
      contrastText: '#000000', // Black text on yellow
    },
    background: {
      default: '#000000',     // Pure black background
      paper: '#0D0D0D',       // Matte black cards
      accent: '#1A1A1A',      // Soft black for secondary areas
    },
    text: {
      primary: '#FFFFFF',     // White text
      secondary: '#B3B3B3',   // Light gray text
      disabled: '#6B6B6B',    // Disabled gray text
      hint: '#FFD700',        // Yellow hint text
    },
    action: {
      active: '#ffbb00',      // Yellow active
      hover: '#FFE55C',       // Soft yellow hover
      selected: '#DAA520',    // Deep gold selected
      disabled: '#4A4A4A',    // Gray disabled
      disabledBackground: '#2D2D2D',
    },
    divider: '#2D2D2D',       // Dark gray dividers
  },
  typography: {
    fontFamily: '"Poppins", "Cairo", "Arial", sans-serif',
    h4: {
      color: '#ffbb00',       // Yellow headings
      fontWeight: 700,
      letterSpacing: '1px',
    },
    h6: {
      color: '#FFFFFF',       // White subheadings
      fontWeight: 600,
    },
    body1: {
      color: '#FFFFFF',
    },
    body2: {
      color: '#B3B3B3',
    },
  },
  shape: {
    borderRadius: 12,
  },
};

//const theme = createTheme(blackYellowTheme);


  return (
    <CacheProvider value={rtlCache}>
        <ThemeProvider theme={theme}>
          <div translate="no" className="app" dir={language === "ar" ? "rtl" : "ltr"}>
          <CssBaseline />
          <GlobalStyles/>
          <InstallApp language={language} />
          <NotificationProvider>
          <CartProvider>    
          <NavBar keywordsData={keywordsData} language={language} toggleLanguage={toggleLanguage}/>
          <ConfirmProvider>
          <ChatProvider>
          <AdminChatProvider>
          <GlobalLoadingDialog language={language} />
          <WelcomePopup isPart={false}/>
          <InstallButton language={language} />
          <Routes>
            <Route path="/admin/chats" element={<ProtectedRoute admin><AdminChatPanel /></ProtectedRoute>} />
            <Route path="/super/chats" element={<ProtectedRoute superAdmin><AdminChatPanel /></ProtectedRoute>} />
            <Route path="/" element={<HomePage keywordsData={keywordsData} language={language}/>} />
            <Route path="/login" element={<PublicRouter><LoginPage keywordsData={keywordsData} language={language}/></PublicRouter>} />
            <Route path="/about" element={<AboutPage keywordsData={keywordsData} language={language}/>} />
            <Route path="/register" element={<PublicRouter><RegisterPage keywordsData={keywordsData} language={language}/></PublicRouter>} />
            <Route path="/product/:id" element={<ProductDetailPage keywordsData={keywordsData} language={language}/>} />
            <Route path="/account" element={<ProtectedRoute><UserAccountPage keywordsData={keywordsData} language={language}/></ProtectedRoute>} />
            <Route path="/wallet" element={<ProtectedRoute><WalletPage keywordsData={keywordsData} language={language}/></ProtectedRoute>} />
            <Route path="/checkout" element={<CheckoutPage keywordsData={keywordsData} language={language}/>} />
            <Route path="/order-confirmation/:orderId" element={<OrderConfirmation keywordsData={keywordsData} language={language}/>} />
            <Route path="/admin" element={<ProtectedRoute admin><AdminDashboard keywordsData={keywordsData} language={language}/></ProtectedRoute>} />
            <Route path="/admin/adminprinters" element={<ProtectedRoute admin><AdminPrinters keywordsData={keywordsData} language={language}/></ProtectedRoute>} />
            <Route path="/admin/users" element={<ProtectedRoute admin><AdminUsers keywordsData={keywordsData} language={language}/></ProtectedRoute>} />
            <Route path="/admin/orders" element={<ProtectedRoute admin><AdminOrdersPage keywordsData={keywordsData} language={language}/></ProtectedRoute>} />
            <Route path="/admin/cafetables" element={<ProtectedRoute admin><CafeTables keywordsData={keywordsData} language={language}/></ProtectedRoute>} />
            <Route path="/admin/paymentsettings" element={<ProtectedRoute admin><PaymentSettings keywordsData={keywordsData} language={language}/></ProtectedRoute>} />
            <Route path="/admin/orders/:orderId" element={<ProtectedRoute admin><OrderDetails keywordsData={keywordsData} language={language}/></ProtectedRoute>} />
            <Route path="/casher/orders" element={<ProtectedRoute casher><AdminOrdersPage keywordsData={keywordsData} language={language}/></ProtectedRoute>} />
            <Route path="/casher/orders/:orderId" element={<ProtectedRoute casher><OrderDetails keywordsData={keywordsData} language={language}/></ProtectedRoute>} />
            <Route path="/auth/success" element={<AuthSuccess keywordsData={keywordsData} language={language}/>} />
            <Route path="/complete-profile" element={<ProtectedRouteWithoutCompletionCheck><CompleteProfile keywordsData={keywordsData} language={language}/></ProtectedRouteWithoutCompletionCheck>} />
            <Route path="/forgot-password" element={<PublicRouter><ForgotPassword keywordsData={keywordsData} language={language}/></PublicRouter>} />
            <Route path="/reset-password/:token" element={<PublicRouter><ResetPassword keywordsData={keywordsData} language={language}/></PublicRouter>} />
            <Route path="/restaurant/:id" element={<RestaurantMenu keywordsData={keywordsData} language={language} />} />
            <Route path="/t/:id/:tableId" element={<RestaurantMenu keywordsData={keywordsData} language={language}/>} />
            <Route path='/shop-owner-request' element={<ProtectedRoute userRole><ShopOwnerRequest keywordsData={keywordsData} language={language} /></ProtectedRoute>}/>
            <Route path='/super' element={<ProtectedRoute superAdmin><AdminMenuEdit keywordsData={keywordsData} language={language} /></ProtectedRoute>}/>
            <Route path='/admin/shop-owner-menu' element={<ProtectedRoute admin><OwnerMenuEdit keywordsData={keywordsData} language={language} /></ProtectedRoute>}/>
            <Route path="/admin/adminprinters/client" element={<ProtectedRoute admin><PrintServiceClient keywordsData={keywordsData} language={language} /></ProtectedRoute>} /> 
            <Route path="/share/restaurant/:restaurantId" element={<ShareRestaurant />} />
            <Route path="/admin/developer-docs" element={<ProtectedRoute admin><DeveloperDocs language={language} /></ProtectedRoute>} />
            <Route path="/my-orders" element={<UserOrdersListPage language={language} />} />
            <Route path="*" element={<NotFound keywordsData={keywordsData} language={language} />} /> {/* Catch-all route */} 
          </Routes>
          <ChatWidget />
          </AdminChatProvider>
          </ChatProvider>
          </ConfirmProvider>
      <CartDrawer />
      </CartProvider>
      </NotificationProvider>
      </div>
    </ThemeProvider>
    </CacheProvider>
  );
}

export default App;